"use client"

import * as React from "react"
import { X } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
        warning:
          "border-yellow-500/50 text-yellow-600 dark:border-yellow-500 dark:text-yellow-400 [&>svg]:text-yellow-600 dark:[&>svg]:text-yellow-400",
        info: "border-blue-500/50 text-blue-600 dark:border-blue-500 dark:text-blue-400 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-400",
        success:
          "border-green-500/50 text-green-600 dark:border-green-500 dark:text-green-400 [&>svg]:text-green-600 dark:[&>svg]:text-green-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface Alert {
  id: string
  title: string
  message: string
  variant?: "default" | "destructive" | "warning" | "info" | "success"
  dismissible?: boolean
  timestamp?: Date
}

export interface SystemAlertBannerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  alerts?: Alert[]
  onDismiss?: (id: string) => void
  maxAlerts?: number
}

const SystemAlertBanner = React.forwardRef<
  HTMLDivElement,
  SystemAlertBannerProps
>(({ className, alerts = [], onDismiss, maxAlerts = 3, ...props }, ref) => {
  const visibleAlerts = alerts.slice(0, maxAlerts)

  if (visibleAlerts.length === 0) {
    return null
  }

  return (
    <div
      ref={ref}
      className={cn("space-y-2", className)}
      {...props}
    >
      {visibleAlerts.map((alert) => (
        <div
          key={alert.id}
          className={cn(alertVariants({ variant: alert.variant }))}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h5 className="font-medium leading-none tracking-tight mb-1">
                {alert.title}
              </h5>
              <div className="text-sm opacity-90">
                {alert.message}
              </div>
              {alert.timestamp && (
                <div className="text-xs opacity-70 mt-1">
                  {alert.timestamp.toLocaleString()}
                </div>
              )}
            </div>
            {alert.dismissible && onDismiss && (
              <button
                onClick={() => onDismiss(alert.id)}
                className="ml-4 inline-flex h-4 w-4 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Dismiss</span>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
})
SystemAlertBanner.displayName = "SystemAlertBanner"

export { SystemAlertBanner, alertVariants }
